import { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '~/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const ERROR_MESSAGES: Record<string, string> = {
  not_member: 'You must be a member of the Discord server to access this app.',
  not_subscribed: 'You need an active subscription to access this app.',
  auth_failed: 'Authentication failed. Please try again.',
  no_code: 'No authorization code received. Please try again.',
  oauth_not_configured: 'Discord OAuth is not configured. Please contact the administrator.',
  guild_not_configured: 'Server configuration error. Please contact the administrator.',
  jwt_not_configured: 'Server configuration error. Please contact the administrator.',
};

export default function LoginPage() {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const error = searchParams.get('error');
  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  useEffect(() => {
    // If already logged in, redirect to intended destination
    if (user && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [user, isLoading, navigate, from]);

  if (isLoading) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  const errorMessage = error ? ERROR_MESSAGES[error] || `Error: ${error}` : null;

  return (
    <div className="min-h-screen app-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Brand hero */}
        <div className="text-center mb-8">
          <div className="relative inline-block -mb-3">
            <div
              className="absolute inset-0 rounded-full blur-2xl opacity-40"
              style={{ background: 'radial-gradient(circle, rgba(224,180,60,0.6), transparent 70%)' }}
            />
            <img
              src="/eagle.png"
              alt="King's Tracker"
              width={512}
              height={512}
              className="relative h-28 w-28 object-contain drop-shadow-[0_8px_24px_rgba(224,180,60,0.45)]"
            />
          </div>
          <h1 className="font-display text-4xl font-bold tracking-wide text-gilded leading-[1.25] pb-1">
            King's Tracker
          </h1>
          <div className="rule-gold h-px w-32 mx-auto my-4" />
          <p className="text-text-secondary text-sm tracking-wide">
            Real-time trading signal intelligence
          </p>
        </div>

        <div className="bg-surface-2/80 backdrop-blur-sm rounded-2xl border border-border p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]">
          {errorMessage && (
            <div className="mb-6 p-4 bg-loss/10 border border-loss/30 rounded-lg">
              <p className="text-loss text-sm">{errorMessage}</p>
            </div>
          )}

          <button
            onClick={() => {
              // Clear stale error from URL before redirecting
              if (error) setSearchParams({}, { replace: true });
              login();
            }}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-medium rounded-lg transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
            Sign in with Discord
          </button>

          {/* Local dev bypass — only rendered by the Vite dev build. The backend
              endpoint is independently hard-gated to non-production. */}
          {import.meta.env.DEV && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] uppercase tracking-widest text-text-muted">dev only</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <a
                href={`${API_BASE}/api/auth/dev-login`}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold hover:bg-gold/10 transition-colors text-sm font-mono tracking-wide"
              >
                Dev login (local admin)
              </a>
            </>
          )}

          <p className="mt-6 text-center text-xs text-text-muted">
            You must be a member of the trading Discord server with an active subscription.
          </p>
        </div>
      </div>
    </div>
  );
}
